import React, { useCallback, useEffect, useMemo } from "react"
import {
    Title,
    Button,
    ModalPanel,
    ExclusiveChoiceDropdown,
    Checkbox,
    SingleLineTextInput,
    Loader
} from "dash-styled"
import { AIModel } from "../../../../../../../../Models/API/AIModel"
import { useBoolean } from "../../../../../../../../Hooks/useBoolean"
import { metaAI } from "neql"
import { useInput } from "../../../../../../../../Hooks/useInput"

interface AddAIModelModalProps {
    selectedModelByName?: AIModel
    selectedModelVersionIndex: number | "Latest"
    models: AIModel[]
    modelChoices: AIModel[]
    isProcessing: boolean
    onClose: () => void
    onSelectModel: (selectedModel: AIModel) => void
    onSubmit: (
        modelId: string,
        usages: metaAI.Meta_Ai_Assignment_Enum[],
        activeLearningThreshold: number
    ) => void
    onSelectModelVersion: (selectedModelVersion: number | "Latest") => void
}

export function AddAIModelModal({
    selectedModelByName,
    selectedModelVersionIndex,
    models,
    modelChoices,
    isProcessing,
    onClose,
    onSubmit,
    onSelectModel,
    onSelectModelVersion
}: AddAIModelModalProps) {
    const thresholdInput = useInput("0.8", true)

    const renderModelChoice = useCallback(
        (model: AIModel) =>
            `${model.name} (${(model.root_id ?? "").slice(-5)})`,
        []
    )
    const handleModelSelection = useCallback(
        (model: AIModel) => onSelectModel(model),
        [onSelectModel]
    )
    const versions = useMemo(
        () =>
            ["Latest"].concat(models.map(model => `version ${model.version}`)),
        [models]
    )
    const versionIndex = useMemo(
        () =>
            selectedModelVersionIndex === "Latest"
                ? 0
                : selectedModelVersionIndex,
        [selectedModelVersionIndex]
    )
    const handleVersionIndexSelection = useCallback(
        (versionIndex: number) =>
            onSelectModelVersion(versionIndex === 0 ? "Latest" : versionIndex),
        [onSelectModelVersion]
    )

    const [isUsedForPreLabeling, , , toggleUsedForPreLabeling] =
        useBoolean(false)
    const [isUsedForActiveLearning, , , toggleUsedForActiveLearning] =
        useBoolean(false)
    const handleSubmit = useMemo(() => {
        const parsedThreshold = Number.parseFloat(thresholdInput.value)

        if (
            selectedModelByName === undefined ||
            Number.isNaN(parsedThreshold) ||
            models.length === 0
        ) {
            return undefined
        }

        return () => {
            onSubmit(
                selectedModelVersionIndex === "Latest"
                    ? models[0].id
                    : models[selectedModelVersionIndex - 1].id,
                [
                    ...(isUsedForPreLabeling
                        ? [metaAI.Meta_Ai_Assignment_Enum.Prelabel]
                        : []),
                    ...(isUsedForActiveLearning
                        ? [metaAI.Meta_Ai_Assignment_Enum.ActiveLearning]
                        : [])
                ],
                parsedThreshold
            )
        }
    }, [
        isUsedForActiveLearning,
        isUsedForPreLabeling,
        models,
        onSubmit,
        selectedModelVersionIndex,
        selectedModelByName,
        thresholdInput.value
    ])

    useEffect(() => {
        onSelectModelVersion("Latest")
    }, [onSelectModelVersion, selectedModelByName])

    return (
        <ModalPanel onClose={onClose} className="max-w-2xl w-full">
            <Title variant="heading2" weight="semibold">
                Add new AI model
            </Title>
            <ExclusiveChoiceDropdown
                label="AI model"
                placeholder="Choose an AI model"
                choices={modelChoices}
                value={selectedModelByName}
                onClick={handleModelSelection}
                renderChoice={renderModelChoice}
            />
            {models.length > 0 ? (
                <ExclusiveChoiceDropdown
                    label="Version"
                    placeholder="Choose a version"
                    choices={versions}
                    selectedIndex={versionIndex}
                    onClick={handleVersionIndexSelection}
                />
            ) : selectedModelByName !== undefined ? (
                <Loader size="xxxl" className="mt-8" />
            ) : null}
            <div className="text-rhino-900">
                <div className="pt-3 text-sm text-rhino-900">Usages</div>
                <Checkbox
                    variant="modern"
                    onChange={toggleUsedForPreLabeling}
                    isChecked={isUsedForPreLabeling}
                >
                    Pre-processing
                </Checkbox>
                <Checkbox
                    variant="modern"
                    onChange={toggleUsedForActiveLearning}
                    isChecked={isUsedForActiveLearning}
                >
                    Active learning
                </Checkbox>
            </div>
            {isUsedForActiveLearning && (
                <SingleLineTextInput
                    label="Active learning threshold"
                    className="block w-48"
                    type="number"
                    transparentBg={true}
                    value={thresholdInput.value}
                    min={0}
                    max={1}
                    step={0.01}
                    onChange={thresholdInput.onChange}
                />
            )}
            <div className="flex justify-center pt-4">
                <Button
                    className="w-auto"
                    disabled={
                        isProcessing ||
                        handleSubmit === undefined ||
                        !thresholdInput.isValid
                    }
                    onClick={handleSubmit}
                >
                    {isProcessing ? "Adding..." : "Add AI model"}
                </Button>
            </div>
        </ModalPanel>
    )
}
